/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect, Fragment } from 'react';
import { isSmallScreen } from 'lib/getScreenWidth';
import createPortal from 'lib/createPortal';
import NonEmptyString from 'lib/NonEmptyString';
import NonEmptyObject from 'lib/NonEmptyObject';
import getModalHeightWithDefault from 'lib/getModalHeightWithDefault';
import cx from 'classnames';
import $ from 'jquery';

type jQueryEventT = {
  target: HTMLElement,
  which: number,
} & Event;

type PropsT = {
  id?: ID,
  className?: string,
  children: React$Node,
  requestClosePortal: Function,
  isOpen: boolean,
  title?: React$Node,
  header?: React$Node,
  footer?: React$Node,
  height?: number | string,
  minHeight?: number | string,
  dialogStyle?: Object,
  headerStyle?: Object,
  bodyStyle?: Object,
  closeOnOutsideClick?: boolean,
  closeOnEsc?: boolean,
  defaultModalHeight?: number,
  naked?: boolean,
  width?: number | string,
};

const modalZ = 1042;

const DEFAULT_MODAL_HEIGHT = 550;

export default (props: PropsT): any | null | React$Node => {
  const {
    id,
    closeOnEsc = true,
    closeOnOutsideClick = true,
    className,
    isOpen,
    title = '',
    header,
    footer,
    headerStyle = {},
    bodyStyle = {},
    children,
    width,
    requestClosePortal,
    defaultModalHeight = DEFAULT_MODAL_HEIGHT,
    naked,
  } = props;
  let { height, minHeight, dialogStyle } = props;
  if (process.env.NODE_ENV === 'test' && naked) {
    return children;
  }

  const baseHeight = height || (isSmallScreen() ? '75vh' : defaultModalHeight);
  height = getModalHeightWithDefault(baseHeight);
  dialogStyle = dialogStyle || {
    width: width || (isSmallScreen() ? '95%' : '800px'),
  };

  const onClose = () => {
    $('body').css('overflow', 'visible');
    requestClosePortal();
  };

  const onEsc = (e: jQueryEventT) => {
    if (!isOpen) return;
    if (e.which === 27 && closeOnEsc === true) {
      onClose();
    }
  };

  const maybeClose = (e: jQueryEventT, id2: ID) => {
    const didClickOutside =
      $(e.target).closest(`#${id2} .modal-dialog`).length === 0;

    if (didClickOutside && closeOnOutsideClick) {
      onClose();
      e.stopPropagation();
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Lock scroll on document body
      $('body').css('overflow', 'hidden');
      // Listen for keydown events
      $(window).on('keydown', onEsc);
    } else {
      // Unlock scroll on document body
      $('body').css('overflow', 'visible');
    }

    return () => {
      // Unlisten from keydown events
      $(window).off('keydown', onEsc);
    };
  }, [isOpen]);

  const closeButton = (
    <button
      type="button"
      className="close p-0"
      data-dismiss="modal"
      aria-label="Close"
      onClick={() => onClose()}
    >
      <span aria-hidden="true">×</span>
    </button>
  );

  let modalHeader =
    header ||
    (NonEmptyString.is(title) || NonEmptyObject.is(title) ? (
      <Fragment>
        <h4 className="modal-title w-100 p-0">{title}</h4>
        {closeButton}
      </Fragment>
    ) : (
      closeButton
    ));
  modalHeader = modalHeader ? (
    <div className="modal-header pt-0 mt-0 pl-0 pr-0" style={headerStyle}>
      {modalHeader}
    </div>
  ) : null;

  const modalFooter = footer ? (
    <div className="modal-footer">{footer}</div>
  ) : null;

  if (!isOpen) return null;

  return createPortal(
    <Fragment>
      <div
        id={id}
        className={cx(
          'modal fade d-flex justify-content-center align-items-center',
          className,
          { show: isOpen }
        )}
        style={{
          zIndex: modalZ,
        }}
        onMouseDown={(e) => {
          id && maybeClose(e, id);
        }}
      >
        <div
          className="modal-dialog"
          role="document"
          style={{
            maxWidth: '90%',
            ...dialogStyle,
          }}
        >
          <div className="modal-content">
            {modalHeader}
            <div
              className="modal-body"
              css={css({
                ...bodyStyle,
                height,
                minHeight,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'none',
              })}
            >
              {props.children}
            </div>
            {modalFooter}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </Fragment>,
    document.body
  );
};
